'use client';

import { ComponentRef, forwardRef, useState } from "react";
import FocusMap, { FocusMapProps } from "@/app/__to_be_integrated/_____components/map/FocusMap";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import { CloseFullscreen, OpenInFull } from "@mui/icons-material";
import { Box, IconButton, Paper, styled } from "@mui/material";
import { useCookieConsent } from "@/app/contexts/cookie-consent-context";
import { BoxProps } from "@mui/system";

interface MiniMapProps extends FocusMapProps {
    slots?: {
        root?: BoxProps;
    }
}

const MiniMap = forwardRef<ComponentRef<typeof FocusMap>, MiniMapProps>(
    ({ children, slots, ...props }, ref) => {

        const { consent } = useCookieConsent();
        const [isMinimapExpanded, setIsMinimapExpanded] = useState(false);

        const onMinimapClicked = () => {
            setIsMinimapExpanded(true);
        }

        const onMinimizeMinimapClicked = () => {
            setIsMinimapExpanded(false);
        }

        return (
            <Box
                className="minimap-root"
                {...slots?.root}
                sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    minHeight: '150px',
                    minWidth: '150px',
                    height: isMinimapExpanded ? '100%' : '150px',
                    width: isMinimapExpanded ? '100%' : '150px',
                    padding: (theme) => theme.spacing(2),
                    transition: 'height 0.3s ease, width 0.3s ease',
                    cursor: 'pointer',
                    zIndex: 500,
                    '&:hover': {
                        height: isMinimapExpanded ? undefined : '175px',
                        width: isMinimapExpanded ? undefined : '175px',
                    },
                    ...(slots?.root?.sx)
                }}
            >
                <Paper
                    onClick={!isMinimapExpanded ? onMinimapClicked : undefined}
                    sx={{
                        position: 'relative',
                        height: '100%',
                        width: '100%',
                        border: (theme) => `1px solid ${theme.palette.primary.main}`,
                        overflow: 'hidden',
                    }}
                >

                    {consent.externalMedia || isMinimapExpanded
                        ? (
                            <FocusMap ref={ref} {...props} zoomControl={!isMinimapExpanded ? false : props.zoomControl || true }>
                                {children}
                            </FocusMap>
                        )
                        : (
                            <MediaView
                                src="/assets/placeholder/google-maps-placeholder.webp"
                                width={100}
                                height={100}
                            />
                        )
                    }


                    {isMinimapExpanded
                        ? (
                            <>
                                <MinMaxButton
                                    aria-expanded={true}
                                    aria-label="Karte verkleinern"
                                    onClick={onMinimizeMinimapClicked}
                                >
                                    <CloseFullscreen/>
                                </MinMaxButton>
                            </>
                        ) : (
                            <>

                                <MinMaxButton
                                    aria-expanded={false}
                                    aria-label="Karte vergrößern"
                                    onClick={onMinimapClicked}
                                >
                                    <OpenInFull/>
                                </MinMaxButton>
                            </>
                        )
                    }
                </Paper>
            </Box>
        )
    }
);

const MinMaxButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: theme.palette.background.paper,
        transform: 'scale(1.1)',
    }
}));

MiniMap.displayName = 'MiniMap';
export default MiniMap;
